import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { forkJoin, pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { LatenessScopeService } from '../infra/lateness-scope.service';
import {
  initialLatenessScopeState,
  LatenessScopeRow,
  LatenessScopeState,
} from '../entities/lateness-scope.model';

const OPS = ['load'] as const;

export const AbsenceLimitScopeStore = signalStore(
  { providedIn: 'root' },
  withState<LatenessScopeState>(initialLatenessScopeState),
  withReqState(...OPS),
  withMethods(
    (store, service = inject(LatenessScopeService), errorStore = inject(ErrorStore)) => ({
      load: rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('load'))),
          switchMap((academicYearId: string) =>
            forkJoin({
              marked: service.listMarkedClasses(academicYearId, 'ApplyAbsenceLimit'),
              total: service.countClasses(academicYearId),
              vacancies: service.vacancies(academicYearId),
            }).pipe(
              tapResponse({
                next: ({ marked, total, vacancies }) => {
                  const countByClass = new Map(
                    vacancies.map((vacancy) => [
                      vacancy.SchoolClassId,
                      vacancy.ActiveEnrollmentCount,
                    ])
                  );

                  const scopeRows: LatenessScopeRow[] = marked.value.map((schoolClass) => ({
                    ...schoolClass,
                    CourseShift: [schoolClass.GradeLevelName, schoolClass.ShiftName]
                      .filter(Boolean)
                      .join(' · '),
                    ActiveEnrollmentCount:
                      countByClass.get(schoolClass.SchoolClassId) ?? null,
                  }));

                  patchState(
                    store,
                    {
                      scopeRows,
                      markedClasses: scopeRows.length,
                      totalClasses: total['@odata.count'] ?? scopeRows.length,
                      studentsWithRule: scopeRows.reduce(
                        (sum, row) => sum + (row.ActiveEnrollmentCount ?? 0),
                        0
                      ),
                    },
                    setPropLoaded('load')
                  );
                },
                error: (e: HttpErrorResponse) => {
                  patchState(store, setPropError('load', e));
                  errorStore.setHttpError(e);
                },
              })
            )
          )
        )
      ),

      blankState(): void {
        patchState(store, initialLatenessScopeState, setPropInit(...OPS));
      },
    })
  )
);
